import React, { useEffect, useRef, useState, memo } from 'react';
import styled from 'styled-components';

const WalletContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 1.5rem auto 0;
  padding: 1.25rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px solid #e9ecef;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1rem -0.5rem 0;
  }
`;

const WalletHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
`;

const WalletTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
`;

const WalletSubtitle = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 1rem;
`;

const WalletButtonContainer = styled.div`
  width: 100%;
  min-height: 48px;
  
  /* Style the wallet button iframe */
  iframe {
    width: 100% !important;
    max-width: 100% !important;
    min-height: 48px !important;
    border: none !important;
    border-radius: 8px !important;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  color: #666;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 0.75rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 12px;
  padding: 1rem;
  margin: 1rem 0;
  color: #856404;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: 1.5rem 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #dee2e6;
  }
  
  span {
    padding: 0 1rem;
    color: #6c757d;
    font-size: 0.85rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

const PaymentSenseWallet = ({
  paymentSession,
  customerDetails,
  onPaymentProcessing,
  onPaymentSuccess,
  onPaymentFailure,
  onPaymentCancel
}) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isWalletReady, setIsWalletReady] = useState(false);
  const [isWalletAvailable, setIsWalletAvailable] = useState(null); // null = checking, true = available, false = not available
  const [error, setError] = useState(null);
  // No processingPayment state needed - Apple Pay/Google Pay handle their own UI
  const walletRef = useRef(null);
  const walletInstanceRef = useRef(null);
  const availabilityTimeoutRef = useRef(null);

  // Load Connect-E script if not already loaded
  useEffect(() => {
    const scriptUrl = paymentSession?.paymentSenseCheckoutUrl;

    if (!scriptUrl) {

      return;
    }

    const scriptId = 'paymentsense-connect-e';

    // Check if script is already loaded
    if (document.getElementById(scriptId)) {

      setIsScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = scriptUrl;
    script.async = true;

    script.onload = () => {

      // Wait for Connect.Wallet to be available
      let attempts = 0;
      const maxAttempts = 50;

      const checkWalletReady = () => {
        attempts++;

        if (window.Connect && window.Connect.Wallet) {

          setIsScriptLoaded(true);
        } else if (attempts >= maxAttempts) {

          setError('Wallet initialization timeout. Please refresh and try again.');
        } else {

          setTimeout(checkWalletReady, 100);
        }
      };

      setTimeout(checkWalletReady, 50);
    };

    script.onerror = () => {

      setError('Failed to load wallet payment. Please try card payment instead.');
    };

    // Only append if not already in the document
    if (!document.getElementById(scriptId)) {
      document.head.appendChild(script);
    }

    return () => {
      // Don't remove script - it's shared with regular Connect-E
    };
  }, [paymentSession?.paymentSenseCheckoutUrl]);

  // Initialize Wallet
  useEffect(() => {
    if (!isScriptLoaded || !paymentSession || walletInstanceRef.current) {
      return;
    }

    if (!window.Connect || !window.Connect.Wallet) {

      return;
    }

    try {
      const displayErrors = (errors) => {
        if (errors && errors.length) {

          const errorMessages = errors.map(e => e.message).join(', ');
          setError(errorMessages);
        } else {
          setError(null);
        }
      };

      const onPaymentComplete = (response) => {

        if (response && (response.statusCode === 0 || response.status === 'Success')) {

          // Extract payment token
          const paymentToken = response.paymentToken || response.id || response.accessToken || paymentSession.paymentSenseJWT;

          // DON'T turn off processing - parent will show "Creating booking..." overlay
          // Similar to regular card payment flow
          
          onPaymentSuccess({
            transactionId: response.transactionId || response.crossReference,
            authCode: response.authCode,
            amount: paymentSession?.amount,
            currency: paymentSession?.currency || 'GBP',
            status: 'Success',
            paymentToken: paymentToken,
            orderId: paymentSession?.orderId,
            paymentMethod: 'wallet', // Identify this as wallet payment
            paymentResult: response
          });
        } else {

          let userFriendlyMessage = response?.message || 'Wallet payment declined';
          
          if (response?.message?.toLowerCase().includes('declined')) {
            userFriendlyMessage = 'Payment was declined. Please try a different payment method.';
          }
          
          setError(userFriendlyMessage);
          // No need to manage processing state - wallet already closed
          
          onPaymentFailure({
            error: userFriendlyMessage,
            code: response?.statusCode,
            details: response
          });
        }
      };

      // Wallet configuration per Connect-E documentation
      const config = {
        containerId: 'wallet-button-container',
        paymentDetails: {
          paymentToken: paymentSession.paymentSenseJWT
        },
        buttonConfig: {
          color: 'black', // or 'white'
          type: 'plain' // plain button without additional text
        },
        emailRequired: true, // Collect email from wallet
        billingAddressRequired: false,
        shippingAddressRequired: false,
        // 3D Secure configuration - critical for Apple Pay/Google Pay authentication challenges
        threeDS: {
          enabled: true,
          challengeWindowSize: '05', // Full-page challenge (600x400px minimum)
          challengePreference: 'NO_PREFERENCE',
          timeout: 600, // 10 minutes timeout for 3DS authentication (prevent polling exceeded)
        },
        callbacks: {
          onPaymentCancelled: () => {

            // No need to manage processing state - wallet UI handles everything
          },
          onPaymentInitiated: () => {

            // Don't show any overlay - Apple Pay/Google Pay have their own native processing UI
            // Only show overlay after payment succeeds during booking creation
          }
        },
        onIframeLoaded: () => {

          setIsWalletReady(true);
          setIsWalletAvailable(true);
          if (availabilityTimeoutRef.current) {
            clearTimeout(availabilityTimeoutRef.current);
          }
        },
        onIframeLoadFailed: (error) => {

          setIsWalletAvailable(false);
          if (availabilityTimeoutRef.current) {
            clearTimeout(availabilityTimeoutRef.current);
          }
        }
      };

      // Ensure container exists
      const container = document.getElementById('wallet-button-container');
      if (!container) {

        setError('Wallet container not found');
        return;
      }

      // Check if wallet already rendered
      if (container.children.length > 0) {

        setIsWalletReady(true);
        return;
      }

      // Initialize the wallet
      const walletInstance = new window.Connect.Wallet(config, displayErrors, onPaymentComplete);
      walletInstanceRef.current = walletInstance;

      // Set a timeout to hide wallet section if button doesn't load
      // (wallet not available in browser/not HTTPS/etc)
      availabilityTimeoutRef.current = setTimeout(() => {
        if (!isWalletReady) {

          setIsWalletAvailable(false);
        }
      }, 5000); // 5 second timeout

    } catch (error) {

      setIsWalletAvailable(false);
    }

    // Cleanup
    return () => {
      if (availabilityTimeoutRef.current) {
        clearTimeout(availabilityTimeoutRef.current);
      }
      if (walletInstanceRef.current) {

        const container = document.getElementById('wallet-button-container');
        if (container) {
          container.innerHTML = '';
        }
        walletInstanceRef.current = null;
      }
    };
  }, [isScriptLoaded, paymentSession?.paymentSenseJWT, isWalletReady]);

  if (!paymentSession) {
    return null;
  }

  // Hide wallet section entirely if determined to be unavailable
  if (isWalletAvailable === false) {

    return null;
  }

  return (
    <>
      <Divider>
        <span>Or Pay with Digital Wallet</span>
      </Divider>

      <WalletContainer>
        <WalletHeader>
          <span style={{ fontSize: '1.5rem' }}>📱</span>
          <div>
            <WalletTitle>Express Checkout</WalletTitle>
          </div>
        </WalletHeader>

        <WalletSubtitle>
          Pay securely with Apple Pay or Google Pay
        </WalletSubtitle>

        {error && (
          <ErrorContainer>
            ⚠️ {error}
          </ErrorContainer>
        )}

        {!isScriptLoaded && !error && (
          <LoadingContainer>
            <div className="spinner"></div>
            <div>Loading wallet options...</div>
          </LoadingContainer>
        )}

        {isScriptLoaded && !error && (
          <WalletButtonContainer>
            <div 
              id="wallet-button-container"
              ref={walletRef}
              style={{
                width: '100%',
                minHeight: '48px'
              }}
            />
            {!isWalletReady && isWalletAvailable === null && (
              <div style={{ 
                textAlign: 'center', 
                color: '#6c757d', 
                fontSize: '0.85rem',
                padding: '1rem' 
              }}>
                <div>Checking wallet availability...</div>
                <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: '#999' }}>
                  Requires HTTPS and Safari (Apple Pay) or Chrome (Google Pay)
                </div>
              </div>
            )}
          </WalletButtonContainer>
        )}

        {/* No processing overlay needed - Apple Pay/Google Pay have their own native UI */}

        <div style={{
          marginTop: '1rem',
          fontSize: '0.75rem',
          color: '#8a8f98',
          textAlign: 'center'
        }}>
          Secure payment powered by Payment Sense • SSL Encrypted • PCI DSS
        </div>
      </WalletContainer>
      
      {/* 3D Secure iframe styling - CRITICAL for wallet authentication challenges to display */}
      <style>{`
        iframe[name*="threeds"],
        iframe[name*="ThreeDS"],
        iframe[name*="3ds"],
        iframe[id*="threeds"],
        iframe[id*="ThreeDS"],
        iframe[id*="3ds"],
        div[id*="threeds"],
        div[id*="ThreeDS"],
        div[id*="3ds"] {
          position: fixed !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          width: 600px !important;
          height: 600px !important;
          max-width: 95vw !important;
          max-height: 95vh !important;
          z-index: 999999 !important;
          background: white !important;
          border: none !important;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.8) !important;
          border-radius: 12px !important;
        }
      `}</style>
    </>
  );
};

export default memo(PaymentSenseWallet);

